import React, { useState, useEffect, useRef } from "react";
import {
  MagnifyingGlassIcon,
  BuildingStorefrontIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Store } from "../../services/api";
import apiService from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";

interface StoreSearchProps {
  onSelect: (store: Store) => void;
  selectedStore?: Store | null;
}

const StoreSearch: React.FC<StoreSearchProps> = ({
  onSelect,
  selectedStore: initialStore,
}) => {
  const [searchText, setSearchText] = useState("");
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(
    initialStore || null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearchText = useDebounce(searchText, 300);

  // Update when external selectedStore changes
  useEffect(() => {
    if (initialStore) {
      setSelectedStore(initialStore);
      setSearchText(initialStore.storeName);
    }
  }, [initialStore]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchStores = async () => {
      if (
        !debouncedSearchText ||
        typeof debouncedSearchText !== "string" ||
        debouncedSearchText.length < 2
      ) {
        setStores([]);
        return;
      }

      setLoading(true);
      try {
        const results = await apiService.searchStores(debouncedSearchText);
        setStores(results || []);
        setIsOpen(true);
      } catch (error: any) {
        console.error("Store search failed.");
        setStores([]);
      } finally {
        setLoading(false);
      }
    };

    searchStores();
  }, [debouncedSearchText]);

  const handleSelect = (store: Store) => {
    setSelectedStore(store);
    setSearchText(store.storeName);
    setIsOpen(false);
    onSelect(store);
  };

  const handleClear = () => {
    setSearchText("");
    setSelectedStore(null);
    setStores([]);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Search Store *
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => searchText && stores.length > 0 && setIsOpen(true)}
          placeholder="Search by store name, city, or category..."
          className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
        />
        {searchText && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {loading && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              Searching...
            </span>
          </div>
        </div>
      )}

      {!loading && isOpen && stores.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {stores.map((store) => (
            <button
              key={store.storeId}
              type="button"
              onClick={() => handleSelect(store)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors ${
                selectedStore?.storeId === store.storeId
                  ? "bg-primary-50 dark:bg-primary-900/20"
                  : ""
              }`}
            >
              <div className="flex items-start space-x-3">
                <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {store.storeName}
                  </p>
                  {(store.street || store.city) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {store.street &&
                        `${store.street} ${store.housenumber || ""}`}
                      {store.street && store.city && " • "}
                      {store.city}
                      {store.country && `, ${store.country}`}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {store.storeCategory && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {typeof store.storeCategory === "object" &&
                        store.storeCategory !== null
                          ? (store.storeCategory as any).name ||
                            (store.storeCategory as any).categoryName ||
                            "Category"
                          : store.storeCategory}
                      </span>
                    )}
                    {store.cashbackRate && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        {store.cashbackRate}% cashback
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Store ID: {store.storeId} • Dealer ID: {store.dealerId}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading &&
        isOpen &&
        searchText &&
        stores.length === 0 &&
        debouncedSearchText &&
        debouncedSearchText.length >= 2 && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              No stores found for "{searchText}"
            </p>
          </div>
        )}

      {selectedStore && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Selected: {selectedStore.storeName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Store ID: {selectedStore.storeId}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Dealer ID: {selectedStore.dealerId}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Type at least 2 characters to search stores
      </p>
    </div>
  );
};

export default StoreSearch;
